/**
 * Shared WebSocket + audio plumbing for Path B (Pipecat) and Path C (Nova Sonic).
 *
 * Both paths use the same browser-side audio loop:
 *   - getUserMedia → AudioContext → ScriptProcessor/Worklet → Int16 PCM 16 kHz
 *   - Binary WebSocket frames out
 *   - Binary WebSocket frames in → AudioBufferSourceNode playback
 *   - Text frames carry transcripts / tool events as JSON
 *
 * The exact wire format differs per path:
 *   - Path B (Pipecat) — Pipecat ProtobufFrameSerializer. To finalize once
 *     vendor keys are in and we exercise the path end-to-end. May involve
 *     adding @pipecat-ai/client-js OR switching the server to JsonFrameSerializer.
 *   - Path C (Nova Sonic) — broker emits raw PCM binary + JSON text events,
 *     wire format documented in agents/nova_sonic/main.py.
 *
 * This class is the common scaffolding both adapters extend. It handles mic
 * capture, WebSocket lifecycle, and audio playback. Subclasses customize the
 * encode/decode functions for their specific wire format.
 */
import { AdapterHandlers, Message, PathAdapter } from "../types";

const SAMPLE_RATE = 16_000;
const FRAME_SIZE = 320; // 20 ms at 16 kHz

export abstract class WebsocketAudioPathAdapter implements PathAdapter {
  protected handlers: AdapterHandlers | null = null;
  protected ws: WebSocket | null = null;
  protected audioCtx: AudioContext | null = null;
  protected mediaStream: MediaStream | null = null;
  protected processor: ScriptProcessorNode | null = null;
  protected playbackCtx: AudioContext | null = null;
  protected playbackQueue: AudioBuffer[] = [];
  protected playing = false;
  protected toolCalls = 0;
  protected sessionStartedAt = 0;

  /** Subclasses provide the URL of their broker. */
  protected abstract getEndpoint(): string;

  /** Encode 16 kHz mono Int16 PCM into a WebSocket frame. */
  protected encodeAudio(pcm: Int16Array): ArrayBuffer | string {
    // Default: pass raw PCM as binary. Pipecat ProtobufFrameSerializer needs
    // an override; Nova Sonic broker accepts raw binary directly.
    // Slice to detach from a possibly-shared buffer (TS narrows .buffer to ArrayBuffer).
    return pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength) as ArrayBuffer;
  }

  /** Handle a binary frame from the server (typically model audio output). */
  protected handleBinary(_data: ArrayBuffer): Int16Array | null {
    // Default: assume PCM int16 frames. Subclasses override if protocol wraps.
    return new Int16Array(_data);
  }

  /** Handle a text frame from the server (transcripts, tool events, status). */
  protected abstract handleText(payload: string): void;

  async start(handlers: AdapterHandlers): Promise<void> {
    this.handlers = handlers;
    this.toolCalls = 0;
    this.sessionStartedAt = performance.now();
    handlers.onStatus("Requesting microphone...");

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      handlers.onError(new Error(`Microphone denied: ${(err as Error).message}`));
      throw err;
    }

    this.audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
    this.playbackCtx = new AudioContext();
    const src = this.audioCtx.createMediaStreamSource(this.mediaStream);

    // ScriptProcessor is deprecated but universally supported; AudioWorklet
    // would be cleaner but needs a separate worklet file. Acceptable for demo.
    this.processor = this.audioCtx.createScriptProcessor(FRAME_SIZE * 4, 1, 1);
    this.processor.onaudioprocess = (e) => {
      const buf = e.inputBuffer.getChannelData(0);
      const pcm = floatToInt16(buf);
      if (this.ws?.readyState === WebSocket.OPEN) {
        const encoded = this.encodeAudio(pcm);
        this.ws.send(encoded);
      }
    };
    src.connect(this.processor);
    this.processor.connect(this.audioCtx.destination);

    handlers.onStatus(`Connecting to ${this.getEndpoint()}...`);
    this.ws = new WebSocket(this.getEndpoint());
    this.ws.binaryType = "arraybuffer";
    this.ws.onopen = () => handlers.onStatus("Connected. Speak whenever you're ready.");
    this.ws.onerror = (e) => handlers.onError(new Error(`WebSocket error: ${(e as ErrorEvent).message || "unknown"}`));
    this.ws.onclose = () => handlers.onStatus("Disconnected.");
    this.ws.onmessage = (e) => {
      if (typeof e.data === "string") {
        this.handleText(e.data);
      } else if (e.data instanceof ArrayBuffer) {
        const pcm = this.handleBinary(e.data);
        if (pcm) this.queueAudio(pcm);
      } else if (e.data instanceof Blob) {
        e.data.arrayBuffer().then((buf) => {
          const pcm = this.handleBinary(buf);
          if (pcm) this.queueAudio(pcm);
        });
      }
    };

    // Wait for either OPEN or ERROR.
    await new Promise<void>((resolve, reject) => {
      const ws = this.ws!;
      ws.addEventListener("open", () => resolve(), { once: true });
      ws.addEventListener("error", () => reject(new Error("WebSocket failed to open")), { once: true });
    });
  }

  async sendText(_text: string): Promise<void> {
    // Voice paths don't need text input. Keep no-op so the adapter satisfies the interface.
  }

  async stop(): Promise<void> {
    try { this.ws?.close(); } catch { /* ignore */ }
    try { this.processor?.disconnect(); } catch { /* ignore */ }
    try { this.mediaStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { await this.audioCtx?.close(); } catch { /* ignore */ }
    try { await this.playbackCtx?.close(); } catch { /* ignore */ }
    this.ws = null;
    this.processor = null;
    this.mediaStream = null;
    this.audioCtx = null;
    this.playbackCtx = null;
    this.playbackQueue = [];
    this.playing = false;
  }

  // ----- helpers ---------------------------------------------------------- //
  protected emit(msg: Omit<Message, "id" | "ts">): void {
    this.handlers?.onMessage({
      id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      ts: Date.now(),
      ...msg,
    });
  }

  protected pushMetric(extra: Partial<{ latencyMs: number; toolCalls: number }>): void {
    this.handlers?.onMetric({
      latencyMs: extra.latencyMs,
      toolCalls: this.toolCalls,
    });
  }

  /** Queue an Int16Array of PCM samples (24 kHz Nova or 16 kHz Pipecat) for playback. */
  protected queueAudio(pcm: Int16Array, sampleRate = 24_000): void {
    if (!this.playbackCtx) return;
    const buf = this.playbackCtx.createBuffer(1, pcm.length, sampleRate);
    const channel = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) channel[i] = pcm[i] / 32768;
    this.playbackQueue.push(buf);
    if (!this.playing) this.drainPlayback();
  }

  private drainPlayback(): void {
    if (!this.playbackCtx || this.playbackQueue.length === 0) {
      this.playing = false;
      return;
    }
    this.playing = true;
    const buf = this.playbackQueue.shift()!;
    const src = this.playbackCtx.createBufferSource();
    src.buffer = buf;
    src.connect(this.playbackCtx.destination);
    src.onended = () => this.drainPlayback();
    src.start();
  }
}

function floatToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}
